import type { MoneyType, CurrencyType } from "@/core/api/graphql/types";
import type { ICurrency } from "@/core/types";

export function formatPrice(
  value: MoneyType | number | undefined,
  currency: ICurrency | CurrencyType,
  displayZero = false,
): string {
  if (value === undefined || value === null) {
    return "N/A";
  }

  const amount = typeof value === "number" ? value : value?.amount;

  if (amount === 0 && !displayZero) {
    return "N/A";
  }

  switch (currency.code) {
    case "USD":
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(amount);

    case "EUR":
      return new Intl.NumberFormat("de-DE", {
        style: "currency",
        currency: "EUR",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(amount);

    case "GBP":
      return new Intl.NumberFormat("en-GB", {
        style: "currency",
        currency: "GBP",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(amount);

    case "SEK": {
      const hasDecimals = amount % 1 !== 0;
      const formattedSEK = new Intl.NumberFormat("sv-SE", {
        minimumFractionDigits: hasDecimals ? 2 : 0,
        maximumFractionDigits: hasDecimals ? 2 : 0,
      })
        .format(amount)
        .replace(/\s/g, " ");

      return `kr ${formattedSEK}`;
    }

    case "CNY":
      return new Intl.NumberFormat("zh-CN", {
        style: "currency",
        currency: "CNY",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(amount);

    case "JPY":
      return new Intl.NumberFormat("ja-JP", {
        style: "currency",
        currency: "JPY",
        minimumFractionDigits: 0, // No cents for JPY
        maximumFractionDigits: 0,
      }).format(amount);

    case "BRL":
      return new Intl.NumberFormat("pt-BR", {
        style: "currency",
        currency: "BRL",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(amount);

    case "INR":
      // en-US locale forces Western 3-digit grouping instead of the Indian lakh/crore grouping
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "INR",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(Math.round(amount));

    default:
      return amount.toString(); // Fallback if the currency isn't listed
  }
}
